import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spin } from '@arco-design/web-react';
import { errorMessage } from '../../../common/api/httpClient';
import type { PortalApplication, PortalIdentity } from '../../../model/portalModels';
import { myTasksApi } from '../../my-tasks/api/myTasksApi';
import { operationsApi } from '../../operations/api/operationsApi';
import { mapOperationLists } from '../../operations/model/operationModels';
import { pagesApi, type PortalPageFavoriteState, type PortalPageIdentity } from '../../pages/api/pagesApi';
import type {
  PortalOperationFavoriteState,
  PortalOperationPageResponse,
  PortalQuickTask,
} from '../../operations/types/portal.types';
import {
  buildQuickTasks,
  buildWorkbenchMetrics,
  buildWorkbenchProfile,
} from '../model/workbenchModels';
import type { PortalMyTaskCounts, PageResult, PortalWorkItemListItem } from '../../my-tasks/model/myTasks';
import PortalWorkbenchView from '../views/PortalWorkbenchView';

type WorkbenchSnapshot = {
  catalog: PortalOperationPageResponse;
  favorites: PortalOperationPageResponse;
  pageFavorites?: PortalPageFavoriteState;
  pageFavoriteError?: string;
  counts: PortalMyTaskCounts;
  todos: PageResult<PortalWorkItemListItem>;
};

export function PortalWorkbenchPage(props: {
  applications: PortalApplication[];
  identity: PortalIdentity;
  onNavigate: (path: string) => void;
  onSessionExpired: () => void;
}) {
  const [snapshot, setSnapshot] = useState<WorkbenchSnapshot>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setError(undefined);
    try {
      const [catalog, favorites, pageResult, counts, todos] = await Promise.all([
        operationsApi.listAll({ availableOnly: true }),
        operationsApi.list({
          page: 1, pageSize: 20,
          favoriteOnly: true, availableOnly: true,
        }),
        pagesApi.favorites()
          .then(state => ({ state, error: undefined }))
          .catch(requestError => ({ state: undefined, error: errorMessage(requestError) })),
        myTasksApi.counts(),
        myTasksApi.list({
          category: 'todo', quickApprovalOnly: true, page: 1, pageSize: 3,
        }),
      ]);
      setSnapshot({ catalog, favorites, pageFavorites: pageResult.state,
        pageFavoriteError: pageResult.error, counts, todos });
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const data = useMemo(() => {
    if (!snapshot) return undefined;
    const operationLists = mapOperationLists(snapshot.catalog);
    const operationIds = new Set(
      operationLists.flatMap(list => list.operations)
        .map(operation => operation.operationId),
    );
    const favoriteOperationIds = mapOperationLists(snapshot.favorites)
      .flatMap(list => list.operations)
      .sort((left, right) =>
        (left.favoriteSortNo ?? Number.MAX_SAFE_INTEGER)
        - (right.favoriteSortNo ?? Number.MAX_SAFE_INTEGER))
      .map(operation => operation.operationId)
      .filter(operationId => operationIds.has(operationId));
    return {
      profile: buildWorkbenchProfile(props.identity),
      metrics: buildWorkbenchMetrics(snapshot.counts),
      operationLists,
      favoriteOperationIds,
      favoritePages: snapshot.pageFavorites?.pages ?? [],
      pageFavoriteError: snapshot.pageFavoriteError,
      quickTasks: buildQuickTasks(snapshot.todos.items),
      notificationPreviews: [],
    };
  }, [props.identity, snapshot]);

  const saveFavorites = async (operationIds: string[]) => {
    if (!snapshot) return;
    const operations = new Map(
      mapOperationLists(snapshot.catalog).flatMap(list => list.operations)
        .map(operation => [operation.operationId, operation]),
    );
    const state: PortalOperationFavoriteState =
      await operationsApi.replaceFavorites({
        version: snapshot.favorites.favoriteVersion,
        operations: operationIds.map(operationId => {
          const operation = operations.get(operationId);
          if (!operation) throw new Error(`操作不存在：${operationId}`);
          return {
            applicationCode: operation.applicationCode,
            operationCode: operation.operationCode,
          };
        }),
      });
    setSnapshot(current => current ? {
      ...current,
      favorites: { ...current.favorites, favoriteVersion: state.version },
    } : current);
    await load();
  };

  const savePageFavorites = async (pages: PortalPageIdentity[]) => {
    if (!snapshot?.pageFavorites) throw new Error('常用界面尚未加载，请刷新后重试');
    const state = await pagesApi.replaceFavorites(snapshot.pageFavorites.version, pages);
    setSnapshot(current => current ? { ...current, pageFavorites: state } : current);
  };

  const reloadPageFavorites = async () => {
    const state = await pagesApi.favorites();
    setSnapshot(current => current ? { ...current, pageFavorites: state,
      pageFavoriteError: undefined } : current);
  };

  const approveQuickTask = async (task: PortalQuickTask) => {
    const detail = await myTasksApi.detail(task.targetTaskId);
    const action = detail.availableActions.find(item =>
      item.actionCode === 'APPROVE'
      && item.operationType === 'WORKFLOW'
      && !item.requiresComment);
    if (!action) {
      throw new Error('该任务不满足快捷审批条件，请进入“我的任务”完成处理。');
    }
    if (!detail.summary.quickApprovable || action.label.trim() !== task.actionLabel) {
      throw new Error('任务快捷审批配置已变化，请刷新后重试。');
    }
    await myTasksApi.action(task.targetTaskId, {
      actionCode: 'APPROVE',
      expectedTaskVersion: detail.summary.runtimeTaskVersion,
      operationId: crypto.randomUUID(),
    });
    await load();
  };

  if (loading && !snapshot) {
    return <div className="portal-operation-workbench-loading">
      <Spin loading tip="正在加载工作台..." />
    </div>;
  }
  if (!data) {
    return <div className="portal-operation-workbench-loading">
      <Alert type="error" content={error ?? '工作台加载失败'} />
      <Button onClick={() => void load()}>重新加载</Button>
    </div>;
  }

  return <PortalWorkbenchView
    applications={props.applications}
    identity={props.identity}
    onSessionExpired={props.onSessionExpired}
    data={data}
    error={error}
    onNavigate={props.onNavigate}
    onOpenOperationList={() => props.onNavigate('/portal/operations')}
    onSaveFavorites={saveFavorites}
    onSavePageFavorites={savePageFavorites}
    onReloadPageFavorites={reloadPageFavorites}
    onApproveQuickTask={approveQuickTask}
  />;
}
